import React, { useState } from 'react';
import Button, { ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { useNotify } from 'react-admin';

/**
 * Props for {@link LoadingButton}.
 */
interface LoadingButtonProps extends Omit<ButtonProps, 'onClick'> {
    /**
     * Handler invoked on click. May return a `Promise`; while it is pending the
     * button shows a spinner, disables itself, and swaps its label for {@link text}.
     *
     * @returns Nothing, or a `Promise` resolved once the async action completes.
     */
    onClick?: () => void | Promise<void>;
    /** Button label / content shown while not loading. */
    children: React.ReactNode;
    /** Label shown in place of `children` while {@link onClick} is pending. Defaults to `'Loading...'`. */
    text?: string;
}

/**
 * A MUI {@link Button} that shows a spinner and disables itself while its
 * (possibly async) `onClick` handler is pending, preventing duplicate clicks.
 * Errors thrown by `onClick` are surfaced via `react-admin`'s `useNotify`.
 */
const LoadingButton: React.FC<LoadingButtonProps> = ({
    onClick,
    children,
    startIcon,
    disabled,
    text = 'Loading...',
    variant = 'contained',
    color = 'primary',
    ...props
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const notify = useNotify();

    const handleClick = async (): Promise<void> => {
        if (isLoading) return; // Prevent multiple clicks while loading
        setIsLoading(true);
        try {
            if (onClick) await onClick();

            setIsLoading(false);
        } catch (error) {
            notify(`Error during promise execution: ${error}`, { type: 'error' });
            setIsLoading(false);
        }
    };

    return (
        <Button
            {...props}
            variant={variant}
            color={color}
            onClick={handleClick}
            disabled={isLoading ? true : disabled} // Disable the button when loading
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : startIcon || null} // Add loading spinner
            style={{ position: 'relative' }} // Position relative for better styling of the spinner
        >
            {isLoading ? text : children}
        </Button>
    );
};

export default LoadingButton;
